import React, { useEffect, useRef } from 'react';
import { Header, Icon, Message, Button, Grid, Form, Segment } from 'semantic-ui-react';
import { formatCepForInput } from '../../services/utils/stringUtils';
import {
  coerceDeliveryAddressStreetNumber,
  DELIVERY_ADDRESS_STREET_NUMBER_HINT,
} from '../../utils/deliveryAddressStreetNumber';
import { normalizeDeliveryAddressStreetNameForInput } from '../../utils/deliveryAddressStreetName';
import type { ClientResponse } from '../../types/dtos';

export interface ContractFormEnderecoTabProps {
  selectedClient: ClientResponse | null;
  cep: string;
  setCep: React.Dispatch<React.SetStateAction<string>>;
  estado: string;
  setEstado: React.Dispatch<React.SetStateAction<string>>;
  cidade: string;
  setCidade: React.Dispatch<React.SetStateAction<string>>;
  endereco: string;
  setEndereco: React.Dispatch<React.SetStateAction<string>>;
  numero: string;
  setNumero: React.Dispatch<React.SetStateAction<string>>;
  bairro: string;
  setBairro: React.Dispatch<React.SetStateAction<string>>;
  complemento: string;
  setComplemento: React.Dispatch<React.SetStateAction<string>>;
  referencia: string;
  setReferencia: React.Dispatch<React.SetStateAction<string>>;
  onCepChange: (value: string) => void;
  cepLoading: boolean;
  fastchip?: boolean;
}

const ESTADOS = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

export const ContractFormEnderecoTab: React.FC<ContractFormEnderecoTabProps> = ({
  selectedClient,
  cep,
  setCep,
  estado,
  setEstado,
  cidade,
  setCidade,
  endereco,
  setEndereco,
  numero,
  setNumero,
  bairro,
  setBairro,
  complemento,
  setComplemento,
  referencia,
  setReferencia,
  onCepChange,
  cepLoading,
  fastchip = false,
}) => {
  const isLocked = !fastchip;

  const fillFromClient = React.useCallback(() => {
    if (selectedClient) {
      setCep(formatCepForInput(selectedClient.cep));
      setEstado(selectedClient.estado || '');
      setCidade(selectedClient.cidade || '');
      setEndereco(selectedClient.logradouro || '');
      setNumero(coerceDeliveryAddressStreetNumber(selectedClient.numero));
      setBairro(selectedClient.bairro || '');
      setComplemento(selectedClient.complemento || '');
    }
  }, [selectedClient, setCep, setEstado, setCidade, setEndereco, setNumero, setBairro, setComplemento]);

  const clearAddress = () => {
    setCep('');
    setEstado('');
    setCidade('');
    setEndereco('');
    setNumero('');
    setBairro('');
    setComplemento('');
    setReferencia('');
  };

  // Quando contrato nao e Fastchip, forcar uso do endereco do cliente da Receita Federal.
  // Roda quando o fastchip muda ou quando o cliente muda; auto-preenche os campos.
  const lastSyncedClientId = useRef<string | null>(null);
  useEffect(() => {
    if (isLocked && selectedClient) {
      const shouldSync =
        lastSyncedClientId.current !== selectedClient.id ||
        cep !== formatCepForInput(selectedClient.cep) ||
        estado !== (selectedClient.estado || '') ||
        cidade !== (selectedClient.cidade || '') ||
        endereco !== (selectedClient.logradouro || '') ||
        numero !== coerceDeliveryAddressStreetNumber(selectedClient.numero) ||
        bairro !== (selectedClient.bairro || '');
      if (shouldSync) {
        fillFromClient();
        lastSyncedClientId.current = selectedClient.id;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked, selectedClient]);

  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="map marker alternate" />
        Endereço de Entrega
      </Header>

      <p style={{ color: '#666', marginBottom: '10px' }}>
        Informe o endereço onde os serviços/produtos do contrato serão entregues.
      </p>

      {isLocked && (
        <Message
          icon
          info
          style={{
            marginBottom: '15px',
            background: 'linear-gradient(135deg, #E8F4FD 0%, #D1E9FB 100%)',
            border: '2px solid #2196F3',
            borderRadius: '8px',
          }}
        >
          <Icon name="lock" />
          <Message.Content>
            <Message.Header>Endereço vinculado ao CNPJ na Receita Federal</Message.Header>
            <p style={{ marginTop: '6px' }}>
              Como este contrato <strong>não é Fastchip</strong>, o chip será enviado pela operadora TIM diretamente para
              o endereço cadastrado do CNPJ na Receita Federal. Os campos abaixo foram preenchidos automaticamente e
              estão bloqueados.
            </p>
            <p style={{ marginTop: '6px', fontSize: '0.95em' }}>
              Para alterar o endereço de entrega, marque a opção <strong>Fastchip</strong> na aba "Contrato".
            </p>
          </Message.Content>
        </Message>
      )}

      <Segment style={{ marginBottom: '20px' }}>
        <Header as="h4" style={{ color: '#2c3e50', marginBottom: '15px' }}>
          <Icon name="map marker alternate" />
          Origem do Endereço
        </Header>

        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <Button color="blue" onClick={fillFromClient} disabled={!selectedClient || isLocked}>
            <Icon name="building" />
            Usar endereço do cliente
          </Button>

          {!isLocked && (
            <Button color="teal" onClick={clearAddress}>
              <Icon name="edit" />
              Informar manualmente
            </Button>
          )}
        </div>

        {selectedClient && (
          <Message info style={{ marginTop: '10px' }}>
            <p>
              <strong>Endereço do cliente:</strong> {selectedClient.logradouro}, {selectedClient.numero} -{' '}
              {selectedClient.bairro}, {selectedClient.cidade}/{selectedClient.estado}
            </p>
          </Message>
        )}
      </Segment>

      <Form>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={6}>
              <Form.Field required>
                <label>CEP</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="00000-000"
                    value={cep}
                    onChange={(e) => onCepChange(e.target.value)}
                    disabled={isLocked}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                      cursor: isLocked ? 'not-allowed' : 'text',
                    }}
                  />
                  {cepLoading && (
                    <div
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      <Icon name="spinner" loading color="blue" />
                    </div>
                  )}
                </div>
                {cepLoading && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                    <Icon name="search" />
                    Buscando endereço...
                  </div>
                )}
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={5}>
              <Form.Field required>
                <label>Estado</label>
                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                  }}
                >
                  <option value="">Selecione...</option>
                  {ESTADOS.map((e) => (
                    <option key={e.value} value={e.value}>
                      {e.label}
                    </option>
                  ))}
                </select>
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={5}>
              <Form.Field required>
                <label>Cidade</label>
                <input
                  type="text"
                  placeholder="Nome da cidade"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                    cursor: isLocked ? 'not-allowed' : 'text',
                  }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={10}>
              <Form.Field required>
                <label>Endereço</label>
                <input
                  type="text"
                  placeholder="Rua, Avenida, etc."
                  value={endereco}
                  onChange={(e) =>
                    setEndereco(normalizeDeliveryAddressStreetNameForInput(e.target.value))
                  }
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                    cursor: isLocked ? 'not-allowed' : 'text',
                  }}
                />
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={3}>
              <Form.Field required>
                <label>Número</label>
                <input
                  type="text"
                  placeholder={fastchip ? '123, 0 ou S/N' : '123'}
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  disabled={isLocked}
                  title={fastchip ? DELIVERY_ADDRESS_STREET_NUMBER_HINT : undefined}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                    cursor: isLocked ? 'not-allowed' : 'text',
                  }}
                />
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={3}>
              <Form.Field>
                <label>Complemento</label>
                <input
                  type="text"
                  placeholder="Sala, Apto"
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                    cursor: isLocked ? 'not-allowed' : 'text',
                  }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={6}>
              <Form.Field required>
                <label>Bairro</label>
                <input
                  type="text"
                  placeholder="Nome do bairro"
                  value={bairro}
                  onChange={(e) => setBairro(e.target.value)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: isLocked ? '#f5f5f5' : '#fff',
                    cursor: isLocked ? 'not-allowed' : 'text',
                  }}
                />
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={10}>
              <Form.Field>
                <label>Ponto de Referência</label>
                <input
                  type="text"
                  placeholder="Próximo ao metrô, shopping, etc."
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>

      <Segment style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>
          <Icon name="map marker" />
          Resumo do Endereço
        </Header>
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <p>
            <strong>CEP:</strong> {cep || 'Não informado'}
            <br />
            <strong>Endereço:</strong> {endereco || 'Não informado'}
            {numero && `, ${numero}`}
            {complemento && ` - ${complemento}`}
            <br />
            <strong>Bairro:</strong> {bairro || 'Não informado'}
            <br />
            <strong>Cidade:</strong> {cidade || 'Não informado'}
            <br />
            <strong>Estado:</strong> {estado || 'Não informado'}
          </p>
          {referencia && (
            <p>
              <strong>Referência:</strong> {referencia}
            </p>
          )}
        </div>
      </Segment>

      <Message info style={{ marginTop: '15px' }}>
        <Message.Header>Dica</Message.Header>
        <p>
          Este endereço será utilizado para entrega dos equipamentos e serviços contratados. Certifique-se de que todos
          os dados estejam corretos.
        </p>
      </Message>
    </div>
  );
};
